import { useCallback, useEffect, useRef, useState } from "react";
import type { ApolloClient } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import {
  FinishToplistSessionDocument,
  MyToplistsDocument,
  MyToplistSessionDocument,
  RestartGameSessionDocument,
  SaveToplistProgressDocument,
  StartGameSessionDocument,
  type GameSessionCoreFragment,
} from "@/graphql/generated/operations";
import type { GameDefinition, GameResult } from "./types";

// One play of a game against genesis: start or resume it, park the game's
// snapshot at checkpoints, count the active play time, and write the result.
// The toplist kind is the only one so far, so its operations are used directly;
// a second kind brings its own load, save and finish operations.

// Steps after which progress is saved even without a checkpoint.
export const SAVE_EVERY_ACTIONS = 10;

export type GameSessionState =
  | { status: "loading" }
  | { status: "error"; error: unknown }
  | { status: "ready"; sessionId: string; snapshot: Record<string, unknown> | null };

export interface GameSession {
  state: GameSessionState;
  progress: (snapshot: object, options?: { checkpoint?: boolean }) => void;
  finish: (result: GameResult) => Promise<void>;
  restart: () => void;
}

interface Running {
  id: string;
  // Play time genesis already has for this session.
  baseMs: number;
  // Play time since the session was opened here, counted only while the tab is visible.
  activeMs: number;
  visibleSince: number | null;
  actions: number;
  // The newest snapshot genesis does not have yet.
  pending: Record<string, unknown> | null;
  finished: boolean;
}

export function useGameSession(game: GameDefinition): GameSession {
  const client = useApolloClient();
  const [state, setState] = useState<GameSessionState>({ status: "loading" });
  const running = useRef<Running | null>(null);
  // Saves run one after another, so an older snapshot never lands after a newer one.
  const queue = useRef<Promise<void>>(Promise.resolve());

  const begin = useCallback((session: GameSessionCoreFragment, snapshot: Record<string, unknown> | null) => {
    running.current = {
      id: session.id,
      baseMs: session.timeTakenMs,
      activeMs: 0,
      visibleSince: isVisible() ? Date.now() : null,
      actions: 0,
      pending: null,
      finished: false,
    };
    setState({ status: "ready", sessionId: session.id, snapshot });
  }, []);

  const save = useCallback((): Promise<void> => {
    const run = async () => {
      const current = running.current;
      if (!current || current.finished || current.pending === null) return;
      const snapshot = current.pending;
      current.pending = null;
      try {
        await client.mutate({
          mutation: SaveToplistProgressDocument,
          variables: { id: current.id, snapshot, snapshotVersion: game.snapshotVersion, timeTakenMs: playTime(current) },
        });
      } catch {
        // Best effort: the next save sends whatever is newest by then.
        current.pending ??= snapshot;
      }
    };
    queue.current = queue.current.then(run);
    return queue.current;
  }, [client, game]);

  useEffect(() => {
    let cancelled = false;
    openSession(client, game).then(
      ({ session, snapshot }) => {
        if (!cancelled) begin(session, snapshot);
      },
      (error: unknown) => {
        if (!cancelled) setState({ status: "error", error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [client, game, begin]);

  useEffect(() => {
    const onVisibilityChange = () => {
      const current = running.current;
      if (!current) return;
      if (isVisible()) {
        current.visibleSince ??= Date.now();
      } else {
        pause(current);
        void save();
      }
    };
    // Best effort: a request sent while the page goes away may not arrive.
    const onPageHide = () => void save();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      // Leaving the game within the app.
      void save();
    };
  }, [save]);

  const progress = useCallback(
    (snapshot: object, options?: { checkpoint?: boolean }) => {
      const current = running.current;
      if (!current || current.finished) return;
      current.pending = snapshot as Record<string, unknown>;
      current.actions += 1;
      if (options?.checkpoint || current.actions % SAVE_EVERY_ACTIONS === 0) void save();
    },
    [save],
  );

  const finish = useCallback(
    async (result: GameResult) => {
      const current = running.current;
      if (!current) throw new Error("No open game session to finish.");
      await save();
      await client.mutate({
        mutation: FinishToplistSessionDocument,
        variables: { id: current.id, entries: result.entries },
        // The profile shows exactly what finishing wrote, without asking again:
        // the domain's toplist swaps its entries for the new ones and keeps its
        // sharing, as genesis does. A first finish creates the toplist, whose id
        // and sharing only genesis knows, so the profile asks again for it.
        update: (cache, { data }) => {
          const existing = cache.readQuery({ query: MyToplistsDocument });
          const myself = existing?.myself;
          if (!data || !myself) return;
          const toplists = myself.toplists ?? [];
          if (!toplists.some((toplist) => toplist.domain === game.target.domain)) {
            cache.evict({ id: cache.identify(myself), fieldName: "toplists" });
            return;
          }
          cache.writeQuery({
            query: MyToplistsDocument,
            data: {
              ...existing,
              myself: {
                ...myself,
                toplists: toplists.map((toplist) =>
                  toplist.domain === game.target.domain ? { ...toplist, entries: data.finishToplistSession } : toplist,
                ),
              },
            },
          });
        },
      });
      current.finished = true;
    },
    [client, game, save],
  );

  const restart = useCallback(() => {
    running.current = null;
    setState({ status: "loading" });
    client.mutate({ mutation: RestartGameSessionDocument, variables: { gameKey: game.key } }).then(
      (result) => begin(required(result.data?.restartGameSession), null),
      (error: unknown) => setState({ status: "error", error }),
    );
  }, [client, game, begin]);

  return { state, progress, finish, restart };
}

// Resumes the open session, or starts one. A snapshot of another version, or
// one the game cannot read, is not guessed at: the play starts over.
async function openSession(
  client: ApolloClient,
  game: GameDefinition,
): Promise<{ session: GameSessionCoreFragment; snapshot: Record<string, unknown> | null }> {
  const started = await client.mutate({ mutation: StartGameSessionDocument, variables: { gameKey: game.key } });
  const session = required(started.data?.startGameSession);
  const { data } = await client.query({
    query: MyToplistSessionDocument,
    variables: { id: session.id },
    fetchPolicy: "network-only",
  });
  const stored = data?.myToplistSession;
  const snapshot = stored?.snapshot ?? null;
  if (snapshot === null) return { session: stored?.session ?? session, snapshot: null };
  if (stored?.snapshotVersion === game.snapshotVersion && game.acceptsSnapshot(snapshot)) {
    return { session: stored.session, snapshot };
  }
  const restarted = await client.mutate({ mutation: RestartGameSessionDocument, variables: { gameKey: game.key } });
  return { session: required(restarted.data?.restartGameSession), snapshot: null };
}

function required<T>(value: T | null | undefined): T {
  if (value == null) throw new Error("genesis answered without a game session.");
  return value;
}

function isVisible(): boolean {
  return document.visibilityState === "visible";
}

function pause(current: Running): void {
  if (current.visibleSince === null) return;
  current.activeMs += Date.now() - current.visibleSince;
  current.visibleSince = null;
}

function playTime(current: Running): number {
  const sinceVisible = current.visibleSince === null ? 0 : Date.now() - current.visibleSince;
  return Math.round(current.baseMs + current.activeMs + sinceVisible);
}
